import React, { useState, useEffect } from 'react';
import systemNotificationService from '../../services/systemNotificationService';
import './SystemNotificationBanner.css';

interface SystemNotificationBannerProps {
  appId: string;
  clientId: string;
  environment?: string;
  userId?: string;
  userRole?: string;
}

interface Notification {
  _id: string;
  title: string;
  message: string;
  type: string;
  severity: string;
  visibilityLevel: string;
  requiresAcknowledgement: boolean;
  acknowledgementText?: string;
  isDismissible: boolean;
  userInteraction: {
    exists: boolean;
    read: boolean;
    acknowledged: boolean;
    dismissed: boolean;
  };
}

const SystemNotificationBanner: React.FC<SystemNotificationBannerProps> = ({
  appId,
  clientId,
  environment = 'production',
  userId,
  userRole
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, [appId, clientId, environment, userId]);

  const fetchNotifications = async () => {
    try {
      const context = {
        clientId,
        appId,
        environment,
        role: userRole,
        externalUserId: userId
      };
      
      const data = await systemNotificationService.getActiveNotifications(context);
      
      // Filter for banner and modal visibility levels
      const bannerNotifications = data.notifications.filter((n: Notification) => {
        // Must be banner or modal
        if (n.visibilityLevel !== 'banner' && n.visibilityLevel !== 'modal') {
          return false;
        }
        
        // If dismissed, don't show
        if (n.userInteraction.dismissed) {
          return false;
        }
        
        // If requires acknowledgement and already acknowledged, don't show
        if (n.requiresAcknowledgement && n.userInteraction.acknowledged) {
          return false;
        }
        
        return true;
      });
      
      setNotifications(bannerNotifications);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setLoading(false);
    }
  };

  const handleAcknowledge = async (notification: Notification) => {
    try {
      const userContext = {
        externalUserId: userId || '',
        appId,
        clientId,
        environment,
        role: userRole
      };
      
      await systemNotificationService.acknowledge(notification._id, userContext);
      
      // Remove from list
      setNotifications(prev => prev.filter(n => n._id !== notification._id));
    } catch (error) {
      console.error('Error acknowledging notification:', error);
    }
  };

  const handleDismiss = async (notification: Notification) => {
    try {
      const userContext = {
        externalUserId: userId || '',
        appId,
        clientId,
        environment,
        role: userRole
      };
      
      await systemNotificationService.dismiss(notification._id, userContext);
      
      // Remove from list
      setNotifications(prev => prev.filter(n => n._id !== notification._id));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const getSeverityClass = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'system-notification-banner-critical';
      case 'warning':
        return 'system-notification-banner-warning';
      case 'info':
      default:
        return 'system-notification-banner-info';
    }
  };

  const getIcon = (type: string, severity: string) => {
    if (severity === 'critical') return '🔥';
    
    switch (type) {
      case 'maintenance':
        return '🔧';
      case 'incident':
        return '⚠️';
      case 'patch':
        return '🩹';
      case 'release':
        return '🚀';
      case 'security':
        return '🔒';
      default:
        return '📢';
    }
  };

  if (loading || notifications.length === 0) {
    return null;
  }

  return (
    <div className="system-notification-banner-container">
      {notifications.map(notification => (
        <div
          key={notification._id}
          className={`system-notification-banner ${getSeverityClass(notification.severity)} ${
            notification.visibilityLevel === 'modal' ? 'system-notification-modal' : ''
          }`}
        >
          <div className="system-notification-banner-content">
            <div className="system-notification-banner-icon">
              {getIcon(notification.type, notification.severity)}
            </div>
            <div className="system-notification-banner-text">
              <div className="system-notification-banner-title">
                {notification.title}
              </div>
              <div className="system-notification-banner-message">
                {notification.message}
              </div>
            </div>
            <div className="system-notification-banner-actions">
              {notification.requiresAcknowledgement && (
                <button
                  className="system-notification-banner-btn system-notification-banner-btn-acknowledge"
                  onClick={() => handleAcknowledge(notification)}
                >
                  {notification.acknowledgementText || 'I understand'}
                </button>
              )}
              {notification.isDismissible && !notification.requiresAcknowledgement && (
                <button
                  className="system-notification-banner-btn system-notification-banner-btn-dismiss"
                  onClick={() => handleDismiss(notification)}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          {notification.visibilityLevel === 'modal' && (
            <div className="system-notification-modal-overlay" />
          )}
        </div>
      ))}
    </div>
  );
};

export default SystemNotificationBanner;
